import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { PaymentConfigOutputDto,ServicesProvider } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { configurationEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to update checkout payment configuration for a services provider
 */
export const updateCheckoutPayment = async (
	servicesProviderId: string,
	data: PaymentConfigOutputDto,
): Promise<ServicesProvider> => {
	const response = await apiClient.post<ServicesProvider>(
		configurationEndpoints.checkoutPayment(servicesProviderId),
		data,
	);
	return response.data;
};

/**
 * Hook for updating checkout payment configuration for a services provider
 */
export const useUpdateCheckoutPayment = () => {
	const servicesProviderId = useCurrentServicesProviderId();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data: PaymentConfigOutputDto) => {
			if (!servicesProviderId) {
				throw new Error(
					'Unable to update checkout payment without a services provider ID.',
				);
			}
			return updateCheckoutPayment(servicesProviderId, data);
		},
		onSuccess: (data) => {
			// Optimistic cache update
			queryClient.setQueryData(queries.servicesProviders.detail(data.id).queryKey, data);
		},
	});
};
